import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/debtors')
const GET_DEBTORS_ENDPOINT = `${API_BASE_URL}/all`
const GET_TOTAL_DEBTORS_ENDPOINT = `${API_BASE_URL}/totalDebtors`
const GET_DEBTOR_ORDERS_ENDPOINT = `${API_BASE_URL}/orders`

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Debtor request failed (${response.status}) at ${url}`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  return response.status === 204 ? null : response.json()
}

function findDebtors(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of ['debtors', 'debtorList', 'results', 'items', 'data']) {
    const debtors = findDebtors(data[key])
    if (debtors.length || Array.isArray(data[key])) return debtors
  }

  return []
}

function findNextCursor(data) {
  if (!data || typeof data !== 'object') return null
  return (
    data.nextCursor ??
    data.next_cursor ??
    data.nextPageToken ??
    data.next_page_token ??
    data.next ??
    findNextCursor(data.debtors) ??
    findNextCursor(data.debtorList) ??
    findNextCursor(data.results) ??
    findNextCursor(data.items) ??
    findNextCursor(data.data) ??
    findNextCursor(data.pagination) ??
    findNextCursor(data.meta) ??
    null
  )
}

const addParam = (params, key, value) => {
  if (value === undefined || value === null || value === '') return
  params.set(key, String(value))
}

export async function getDebtors({
  cursor = null,
  page = 1,
  customer = '',
  customerType = '',
  orderType = '',
  salesExecutive = '',
} = {}) {
  const params = new URLSearchParams({ limit: '20' })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))
  addParam(params, 'customer', customer)
  addParam(params, 'customerType', customerType)
  addParam(params, 'orderType', orderType)
  addParam(params, 'salesExecutive', salesExecutive)

  const data = await request(`${GET_DEBTORS_ENDPOINT}?${params}`)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const debtors = findDebtors(data)
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ?? payload?.currentPage ?? payload?.current_page ?? page,
  )
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    data?.hasNext ??
    data?.has_next ??
    nextCursor,
  )

  return {
    debtors,
    nextCursor,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
    currentPage,
  }
}

export async function getTotalDebtors() {
  const data = await request(GET_TOTAL_DEBTORS_ENDPOINT)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data

  if (typeof data === 'number' || typeof data === 'string') {
    return Number(data) || 0
  }

  return Number(
    payload?.totalDebtors ??
      payload?.total_debtors ??
      payload?.total ??
      data?.totalDebtors ??
      data?.total_debtors ??
      data?.total ??
      0,
  )
}

export async function getDebtorOrders({ customer, orderType = '' } = {}) {
  const params = new URLSearchParams()
  addParam(params, 'customer', customer)
  addParam(params, 'orderType', orderType)

  const data = await request(`${GET_DEBTOR_ORDERS_ENDPOINT}?${params}`)
  return findDebtors(data)
}
